import { gridREMLayout, gridREMLayoutRows } from '@constants/layout';
import { Color } from '@lib/sanity/sanity.types';
import { ThemeProjection, VisualsProjection } from '@lib/sanity/types/common';
import Visuals from '@ui/Visuals/Visuals';
import cn from '@utils/cn';
import { ReactNode } from 'react';

export type ContainerProps = {
  id?: string;
  theme?: ThemeProjection | null;
  children: ReactNode;
  className?: string;
  childClassName?: string;
  innerClassName?: string;
  backgroundClassName?: string;
  style?: React.CSSProperties;
  removeContainer?: boolean;
  paddingType?: 'none' | 'xsmall' | 'small' | 'large' | 'horizontalOnly';
  backgroundVisuals?: VisualsProjection | null;
  useRows?: boolean;
};

export default function Container({
  id,
  theme,
  className,
  childClassName,
  children,
  removeContainer,
  paddingType = 'large',
  useRows = false,
  style,
  backgroundVisuals,
  backgroundClassName,
}: ContainerProps) {
  const containerStyles: React.CSSProperties = {
    ...style,
    ...renderThemeTheme(theme?.theme),
    ...renderThemeAccent(theme?.accent),
  };

  return (
    <section id={id} className={cn('relative flex w-full', className)} style={containerStyles}>
      {backgroundVisuals && (
        <Visuals
          data={backgroundVisuals}
          className={cn('absolute z-0 hidden h-full w-full object-cover sm:block', backgroundClassName)}
        />
      )}

      {removeContainer ? (
        children
      ) : (
        <div
          className={cn(
            gridREMLayout,
            {
              [gridREMLayoutRows]: useRows,
              'px-4 sm:px-8': paddingType === 'horizontalOnly',
              'px-4 py-8 sm:px-8': paddingType === 'xsmall',
              'px-4 py-16 sm:px-8': paddingType === 'small',
              'px-4 py-16 sm:px-8 md:py-24': paddingType === 'large',
            },
            childClassName,
          )}
        >
          {children}
        </div>
      )}
    </section>
  );
}

// Filter out empty/falsy values to avoid breaking CSS variable cascade
const filterEmptyValues = (obj: Record<string, string | null | undefined | false>) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v)) as React.CSSProperties;

export function renderThemeTheme(theme?: ThemeProjection['theme']) {
  if (!theme) {
    return {};
  }

  return filterEmptyValues({
    '--background': getRGBA(theme?.background),
    '--foreground': getRGBA(theme?.header),
    '--body-foreground': getRGBA(theme?.description),
    '--secondary': getRGBA(theme?.buttonSecondaryBackground),
    '--border': getRGBA(theme?.border),
    '--placeholder': getRGBA(theme?.placeholder),
    '--tertiary': getRGBA(theme?.tertiary),
    '--tertiary-foreground': getRGBA(theme?.tertiaryForeground),
    '--input': getRGBA(theme?.input),
  });
}

export function renderThemeAccent(accent?: ThemeProjection['accent']) {
  if (!accent) {
    return {};
  }

  return filterEmptyValues({
    '--primary': getRGBA(accent?.buttonPrimaryBackground),
    '--primary-foreground': getRGBA(accent?.buttonPrimaryText),
    '--primary-no-alpha': getRGBA(accent?.buttonPrimaryBackground),
    '--accent-foreground': getRGBA(accent?.unicase),
    '--link': getRGBA(accent?.url),
  });
}

export const getRGBA = (color?: Color | null): string =>
  color ? `rgba(${color?.rgb?.r},${color?.rgb?.g},${color?.rgb?.b},${color?.rgb?.a ?? 1})` : '';
